
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import AuthGuard from '@/components/auth/AuthGuard';
import { Database } from '@/integrations/supabase/types';
import { BlogPostForm } from '@/components/blog/BlogPostForm';
import { useBlogPostForm } from '@/hooks/blog/useBlogPostForm';

type ContentStatus = Database['public']['Enums']['content_status'];

const NewBlogPost = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const {
    formData,
    selectedItineraryIds,
    setSelectedItineraryIds,
    selectedPackingListId,
    setSelectedPackingListId,
    selectedTagIds,
    setSelectedTagIds,
    activeTab,
    setActiveTab,
    loading,
    setLoading,
    handleChange,
    handleSelectChange,
    handleImageUploaded,
    handleDateTimeChange,
    handleContentChange
  } = useBlogPostForm();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title || !formData.slug) {
      toast.error('Title and slug are required');
      return;
    }
    
    setLoading(true);
    
    try {
      // First, insert the blog post with added SEO fields and scheduled_at
      const { data, error } = await supabase
        .from('blog_posts')
        .insert({
          title: formData.title,
          slug: formData.slug,
          excerpt: formData.excerpt,
          content: formData.content,
          featured_image: formData.featured_image,
          status: formData.status,
          author_id: user?.id,
          // Add SEO fields
          seo_title: formData.seo_title,
          seo_description: formData.seo_description,
          seo_keywords: formData.seo_keywords,
          // Add scheduled_at field
          scheduled_at: formData.scheduled_at,
        })
        .select();
      
      if (error) {
        throw error;
      }
      
      const blogPostId = data[0].id;
      
      // Then, if there are selected itineraries, create the connections
      if (selectedItineraryIds.length > 0) {
        const itineraryLinks = selectedItineraryIds.map(itineraryId => ({
          blog_post_id: blogPostId,
          itinerary_id: itineraryId
        }));
        
        const { error: linkError } = await supabase
          .from('blog_itinerary_links')
          .insert(itineraryLinks);
          
        if (linkError) {
          console.error('Error linking itineraries:', linkError);
          toast.error(`Blog post created, but there was an error linking itineraries: ${linkError.message}`);
        }
      }
      
      // Link tags if selected
      if (selectedTagIds.length > 0) {
        const tagLinks = selectedTagIds.map(tagId => ({
          blog_post_id: blogPostId,
          tag_id: tagId
        }));
        
        const { error: tagLinkError } = await supabase
          .from('blog_posts_tags')
          .insert(tagLinks);
          
        if (tagLinkError) {
          console.error('Error linking tags:', tagLinkError);
          toast.error(`Blog post created, but there was an error linking tags: ${tagLinkError.message}`);
        }
      }
      
      // Link packing list if selected
      if (selectedPackingListId) {
        const { error: packingListLinkError } = await supabase
          .from('blog_packing_list_links')
          .insert({
            blog_post_id: blogPostId,
            packing_list_id: selectedPackingListId
          });
          
        if (packingListLinkError) {
          console.error('Error linking packing list:', packingListLinkError);
          toast.error(`Blog post created, but there was an error linking packing list: ${packingListLinkError.message}`);
        }
      }
      
      // Show appropriate success message based on status
      if (formData.status === 'scheduled') {
        toast.success(`Blog post scheduled for publication on ${new Date(formData.scheduled_at!).toLocaleString()}`);
      } else {
        toast.success('Blog post created successfully');
      }
      
      navigate('/dashboard/blogs');
    } catch (error: any) {
      toast.error(`Error creating blog post: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <AuthGuard requireEditor>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">New Blog Post</h1>
            <p className="text-muted-foreground">Create and publish a new blog post</p>
          </div>
        </div>
        
        <BlogPostForm
          formData={formData}
          selectedItineraryIds={selectedItineraryIds}
          setSelectedItineraryIds={setSelectedItineraryIds}
          selectedPackingListId={selectedPackingListId}
          setSelectedPackingListId={setSelectedPackingListId}
          selectedTagIds={selectedTagIds}
          setSelectedTagIds={setSelectedTagIds}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          loading={loading}
          handleChange={handleChange}
          handleSelectChange={handleSelectChange}
          handleImageUploaded={handleImageUploaded}
          handleDateTimeChange={handleDateTimeChange}
          handleContentChange={handleContentChange}
          onSubmit={handleSubmit}
          onCancel={() => navigate('/dashboard/blogs')}
          blogPostId="temporary" // Will be replaced with actual ID after creation
        />
      </div>
    </AuthGuard>
  );
};

export default NewBlogPost;
